import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Row, Tag } from 'antd';
import { ArrowLeftOutlined, ClockCircleOutlined, EditOutlined, PlayCircleOutlined, RadarChartOutlined } from '@ant-design/icons';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import Loader from 'components/loader';
import { getDateFormat } from 'services/Utils';
import { DATE_FORMAT } from 'constants/app-constants';
import { useI18n } from 'hooks/useI18n';
import {
	translateWorkoutGender,
	translateWorkoutLevel,
	translateWorkoutMuscleGroup,
} from 'helpers/workoutI18n';

import './workout-details.scss';

const LEVEL_CLASS_MAP = {
	Beginner: 'workout-details__tag--beginner',
	Intermediate: 'workout-details__tag--intermediate',
	Advanced: 'workout-details__tag--advanced',
};

const GENDER_CLASS_MAP = {
	Male: 'workout-details__tag--male',
	Female: 'workout-details__tag--female',
	Unisex: 'workout-details__tag--unisex',
};

const getWorkoutEntity = (payload) => {
	if (!payload) {
		return null;
	}

	if (Array.isArray(payload?.data)) {
		return payload.data[0] || null;
	}

	if (payload?.data && !Array.isArray(payload.data)) {
		return payload.data;
	}

	return payload;
};

const formatDetailValue = (value, fallback) => value || fallback;

const WorkoutDetails = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutsRedux = useSelector((state) => state.workoutsRedux);

	useEffect(() => {
		dispatch(getApi('GET_WORKOUT_BY_ID', `${SERVER_IP}api/workouts/${id}`));
	}, [id, dispatch]);

	const workout = useMemo(() => getWorkoutEntity(workoutsRedux?.currentWorkout), [workoutsRedux?.currentWorkout]);
	const loading = globalRedux.apiStatus.GET_WORKOUT_BY_ID === 'PENDING';
	const detailItems = useMemo(
		() => [
			{ label: t('workouts.details.muscle_group'), value: translateWorkoutMuscleGroup(t, workout?.muscleGroup, t('workouts.common.not_provided')) },
			{ label: t('workouts.details.equipment'), value: formatDetailValue(workout?.equipment, t('workouts.common.not_provided')) },
			{ label: t('workouts.details.default_reps'), value: formatDetailValue(workout?.defaultReps, t('workouts.common.not_provided')) },
			{ label: t('workouts.details.created_on'), value: workout?.created_at ? getDateFormat(workout.created_at, DATE_FORMAT.DD_MM_YYYY) : t('workouts.common.not_available') },
			{ label: t('workouts.details.updated_on'), value: workout?.updated_at ? getDateFormat(workout.updated_at, DATE_FORMAT.DD_MM_YYYY) : t('workouts.common.not_available') },
		],
		[workout, t]
	);

	if (loading) return <Loader />;
	if (!workout) {
		return (
			<div className="workout-details workout-details--empty">
				<Card className="workout-details__empty-card" bordered={false}>
					<h2 className="workout-details__empty-title">{t('workouts.details.empty_title')}</h2>
					<p className="workout-details__empty-copy">{t('workouts.details.empty_copy')}</p>
					<Button type="primary" onClick={() => navigate('/workouts')}>
						{t('workouts.details.back_to_workouts')}
					</Button>
				</Card>
			</div>
		);
	}

	return (
		<div className="workout-details">
			<div className="workout-details__shell">
				<div className="workout-details__header-row">
					<Button className="workout-details__back-btn" icon={<ArrowLeftOutlined />} onClick={() => navigate('/workouts')}>
						{t('common.back')}
					</Button>
					<Button type="primary" className="workout-details__edit-btn" icon={<EditOutlined />} onClick={() => navigate(`/workouts/edit/${id}`)}>
						{t('workouts.details.edit_exercise')}
					</Button>
				</div>

				<Card className="workout-details__hero" bordered={false}>
					<div className="workout-details__hero-copy">
						<span className="workout-details__eyebrow">{t('workouts.details.eyebrow')}</span>
						<h1 className="workout-details__title">{workout.name}</h1>
						<p className="workout-details__subtitle">
							{t('workouts.details.subtitle')}
						</p>
						<div className="workout-details__tags">
							<Tag className={`workout-details__tag ${GENDER_CLASS_MAP[workout.gender] || 'workout-details__tag--neutral'}`}>{translateWorkoutGender(t, workout.gender, t('workouts.common.not_provided'))}</Tag>
							<Tag className={`workout-details__tag ${LEVEL_CLASS_MAP[workout.level] || 'workout-details__tag--neutral'}`}>{translateWorkoutLevel(t, workout.level, t('workouts.common.not_provided'))}</Tag>
							<Tag className="workout-details__tag workout-details__tag--neutral">{translateWorkoutMuscleGroup(t, workout.muscleGroup, t('workouts.common.not_provided'))}</Tag>
						</div>
					</div>
					<div className="workout-details__hero-stats">
						<div className="workout-details__stat-card">
							<div className="workout-details__stat-icon"><RadarChartOutlined /></div>
							<div>
								<div className="workout-details__stat-value">{workout.defaultSets || 0} x {workout.defaultReps || '-'}</div>
								<div className="workout-details__stat-label">{t('workouts.details.default_volume')}</div>
							</div>
						</div>
						<div className="workout-details__stat-card">
							<div className="workout-details__stat-icon"><ClockCircleOutlined /></div>
							<div>
								<div className="workout-details__stat-value">{workout.defaultRestSeconds || 0}s</div>
								<div className="workout-details__stat-label">{t('workouts.details.rest_time')}</div>
							</div>
						</div>
					</div>
				</Card>

				<Row gutter={[20, 20]}>
					<Col xs={24} xl={15}>
						<Card className="workout-details__panel" title={t('workouts.details.workout_profile')} bordered={false}>
							<div className="workout-details__grid">
								{detailItems.map((item) => (
									<div key={item.label} className="workout-details__detail-card">
										<div className="workout-details__detail-label">{item.label}</div>
										<div className="workout-details__detail-value">{item.value}</div>
									</div>
								))}
							</div>
						</Card>
					</Col>
					<Col xs={24} xl={9}>
						<Card className="workout-details__panel" title={t('workouts.details.links_and_notes')} bordered={false}>
							<div className="workout-details__stack">
								<div className="workout-details__info-card">
									<div className="workout-details__info-title">{t('workouts.details.video_reference')}</div>
									{workout.videoUrl ? (
										<a className="workout-details__video-link" href={workout.videoUrl} target="_blank" rel="noreferrer">
											<PlayCircleOutlined />
											<span>{t('workouts.details.open_exercise_video')}</span>
										</a>
									) : (
										<div className="workout-details__empty-copy">{t('workouts.details.no_video_url')}</div>
									)}
								</div>
								<div className="workout-details__info-card">
									<div className="workout-details__info-title">{t('workouts.form.fields.instructions')}</div>
									<div className="workout-details__instructions">{workout.instructions || t('workouts.details.no_instructions')}</div>
								</div>
							</div>
						</Card>
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default WorkoutDetails;
